import React from 'react';
import { useSelector } from 'react-redux';
import styles from '../search.page.module.scss';
import { Button } from 'src/components/design-system';
import { EVENTS, SEARCH_TYPE } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import analyticsService from 'src/helpers/analytics.service';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { useShowMoreResults } from './use-show-more-results';

// Search-v2 counterpart to SearchImprovedResults: the fuzzy-only tier the API now
// returns as `extended`, collapsed behind a toggle at the end of its section. Once
// open it is paged like the exact tier, so a group with only related resources
// never dumps the whole bucket at once.
// Entries arrive pre-sorted by Atlas score (descending) — unlike the v1 tier, which
// sorts ascending because Fuse treats 0 as a perfect match. Do not re-sort here.
const SearchV2ExtendedResults = ({
  entries = [],
  renderByType,
  hasLiteralResults,
  sectionContext,
  resetKey,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const langScreen = useSelector(getLang('SEARCH_SCREEN'));
  const searchSessionId = useSelector(state => state.search.sessionId);
  const term = useSelector(state => state.search.term);
  const paging = useShowMoreResults({
    total: entries.length,
    resetKey,
    searchType: SEARCH_TYPE.EXTENDED,
    section: sectionContext?.section,
    sectionType: sectionContext?.sectionType,
    sectionPosition: sectionContext?.sectionPosition,
  });

  if (entries.length === 0) return null;

  // With no literal hits the section would be empty when collapsed, so keep it
  // open and drop the toggle entirely.
  const isExpanded = expanded || !hasLiteralResults;

  const toggle = () => {
    if (!expanded) {
      analyticsService.track(EVENTS.SEARCH.SHOW_MORE, {
        term,
        searchSessionId,
        contextId: searchContextIdManager.getContextId(),
        resultsCount: sectionContext?.literalResultsCount || 0,
        extendedResultsCount: entries.length,
      });
    }
    setExpanded(prev => !prev);
  };

  return (
    <>
      {isExpanded &&
        entries.slice(0, paging.visibleCount).map(({ type, item }, index) => {
          const render = renderByType[type];
          if (!render) return null;
          const itemOptions = {
            ...sectionContext,
            searchType: SEARCH_TYPE.EXTENDED,
            extendedResultsCount: entries.length,
            position: (sectionContext?.positionOffset || 0) + index + 1,
          };
          return (
            <React.Fragment key={`extended-${type}-${item.id || index}`}>
              {render({ item }, index, itemOptions)}
            </React.Fragment>
          );
        })}

      {isExpanded && paging.remaining > 0 && (
        <div className={styles.extendedResults}>
          <Button type="link" onClick={paging.onShowMore}>
            {paging.label}
          </Button>
        </div>
      )}
      {paging.liveRegion}

      {hasLiteralResults && (
        <div className={styles.extendedResults}>
          <Button type="link" onClick={toggle}>
            {expanded
              ? langScreen.SHOW_LESS
              : langScreen.SHOW_ADDITIONAL_RESULTS.replace(
                  '{number}',
                  formatNumber(entries.length),
                )}
          </Button>
        </div>
      )}
    </>
  );
};

export { SearchV2ExtendedResults };
